import React, { useMemo, useEffect } from 'react';
import SchemaPaper from './SchemaPaper';
import { FullSizeColumn } from '../FlexBox';
import { useSetAtom } from 'jotai';
import { genericFormAtom } from '../../store/genericFormStore';

// Defaults read the wave-filter shape. They live at module scope so their
// identity is stable and the memo below only recomputes on real data changes.
const defaultGetGroupId = (group) => group.sectionId;
const defaultGetItems = (group) => group.items || [];
const defaultGetItemId = (item) => item.waveFilterId;
const defaultGetItemSchema = (item) => item.uiSchemaConfiguration;
const defaultGetItemPath = (groupId, itemId) => `${groupId}.${itemId}`;
const defaultFindStoredParameter = (parameters, item, groupId) =>
  parameters.find(
    (param) =>
      param.waveFilterId === item.waveFilterId &&
      (param.sectionId ? param.sectionId === groupId : true)
  );

/**
 * The accessors and flags of a panel drawing one headerless, always-on schema per group, from groups
 * shaped `{ id, items: [{ id, fields }] }` with no stored parameters. Spread onto the wrapper.
 */
export const SINGLE_SCHEMA = {
  getGroupId: (group) => group.id,
  getItems: (group) => group.items,
  getItemId: (item) => item.id,
  getItemSchema: (item) => item.fields,
  findStoredParameter: () => undefined,
  showHeader: false,
  showToggle: false,
};

/**
 * Renders groups of schema-driven papers and keeps their values in the generic
 * form store. Any caller can drive it by supplying the accessors that map its
 * own data onto the group/item/schema contract.
 *
 * @param {Array} groups - Groups to render; falls back to `dynamicFilters`.
 * @param {Function} formatSchemaCallback - (schema, storedData, path) => fields.
 * @param {*} tabValue - Group id currently visible; other groups are hidden.
 * @param {Array} filterParameters - Stored values matched onto each item.
 * @param {Function} onToggle - (groupId, itemId, commit) => void, active switch.
 * @param {Boolean} showHeader - Renders each item's name and description.
 * @param {Boolean} showToggle - Renders each item's active switch.
 */
const SchemaWrapper = function SchemaWrapper({
  dynamicFilters,
  groups = null,
  formatSchemaCallback,
  tabValue,
  filterParameters = [],
  onToggle,
  getGroupId = defaultGetGroupId,
  getItems = defaultGetItems,
  getItemId = defaultGetItemId,
  getItemSchema = defaultGetItemSchema,
  getItemPath = defaultGetItemPath,
  findStoredParameter = defaultFindStoredParameter,
  showHeader = true,
  showToggle = true,
}) {
  const resetAtom = useSetAtom(genericFormAtom);

  const sourceGroups = groups ?? dynamicFilters;

  const formattedSections = useMemo(() => {
    return (sourceGroups || []).map((group) => {
      const groupId = getGroupId(group);

      const formattedItems = getItems(group).map((item) => {
        const itemId = getItemId(item);
        const currentGroupId = groupId || item.sectionId;
        const dynamicPath = getItemPath(currentGroupId, itemId);

        const uiSchema = getItemSchema(item);

        const matchedParam = findStoredParameter(
          filterParameters,
          item,
          currentGroupId
        );

        const storedData = matchedParam?.filtersParameters;

        const finalSchema = formatSchemaCallback
          ? formatSchemaCallback(uiSchema, storedData, dynamicPath)
          : uiSchema
            ? typeof uiSchema === 'string'
              ? JSON.parse(uiSchema)
              : uiSchema
            : [];

        return {
          ...item,
          itemId,
          // Calculate the initial active state from the API
          initialActive:
            matchedParam?.active !== undefined
              ? matchedParam.active
              : item.active,
          dynamicPath,
          finalSchema,
        };
      });

      return {
        ...group,
        groupId,
        items: formattedItems,
      };
    });
  }, [
    sourceGroups,
    formatSchemaCallback,
    filterParameters,
    getGroupId,
    getItems,
    getItemId,
    getItemSchema,
    getItemPath,
    findStoredParameter,
  ]);

  useEffect(() => {
    return () => {
      resetAtom({});
    };
  }, [resetAtom]);

  return (
    <>
      {formattedSections.map(({ groupId, items }) => (
        <FullSizeColumn
          key={groupId}
          gap="24px"
          padding="8px"
          display={tabValue === groupId ? 'flex!important' : 'none!important'}
        >
          {items.map((item) => (
            <SchemaPaper
              key={item?.dynamicPath}
              sectionId={groupId}
              waveFilterId={item?.itemId}
              name={item?.name}
              description={item?.description}
              active={item.initialActive}
              uiSchemaConfiguration={item?.finalSchema ?? []}
              dynamicPath={item?.dynamicPath}
              onToggle={onToggle}
              showHeader={showHeader}
              showToggle={showToggle}
            />
          ))}
        </FullSizeColumn>
      ))}
    </>
  );
};

export default SchemaWrapper;
